import type { JobScopeItem, ReadinessChecklist, RiskFlag } from "../types";

const requiredCategories = ["SIZE", "ACTIVITY", "LOCATION", "TIME", "EQUIPMENT"] as const;

export function calculateReadiness(
  scopeItems: JobScopeItem[],
  riskFlags: RiskFlag[],
): ReadinessChecklist {
  const missingItems: string[] = [];
  const blockers: string[] = [];

  for (const category of requiredCategories) {
    const item = scopeItems.find((candidate) => candidate.category === category);
    if (!item || item.description.trim().length < 8 || !item.isVerified) {
      missingItems.push(category);
    }
  }

  for (const flag of riskFlags) {
    if (flag.severity === "RED") {
      blockers.push(flag.label);
    }
  }

  const verifiedCount = scopeItems.filter(
    (item) => item.isVerified && item.description.trim().length >= 8,
  ).length;
  const baseScore = Math.round((verifiedCount / requiredCategories.length) * 100);
  const riskPenalty = Math.min(
    35,
    riskFlags.filter((flag) => flag.severity !== "GREEN").length * 10,
  );
  const blockerPenalty = blockers.length > 0 ? 20 : 0;
  const score = Math.max(0, Math.min(100, baseScore - riskPenalty - blockerPenalty));

  if (blockers.length > 0 || score < 50) {
    return {
      level: "RED",
      score,
      missingItems,
      blockers,
      guidance:
        "Do not send this job until safety warnings are acknowledged and details are clear.",
    };
  }

  if (missingItems.length > 0 || score < 85) {
    return {
      level: "AMBER",
      score,
      missingItems,
      blockers,
      guidance: "Clarify missing details before a contractor commits time, tools, or travel.",
    };
  }

  return {
    level: "GREEN",
    score,
    missingItems,
    blockers,
    guidance: "The job is clear enough for a contractor to decide whether it is a fit.",
  };
}
